import { createClient } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL!;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY!;

/**
 * The web app must never refresh a session. The extension is the sole holder.
 *
 * /connect hands the extension THIS session - the same session_id, the same
 * refresh token. GoTrue rotates refresh tokens and treats a reused one as
 * theft: it revokes the whole family. So if both sides refresh, whichever goes
 * second presents a spent token and both are signed out. That is not
 * theoretical - it is what happened on 2026-08-29, after a machine woke from
 * sleep and the page and the service worker refreshed 38 seconds apart:
 *
 *   15:24:35  POST /token  200
 *   15:25:13  POST /token  400  refresh_token_already_used  "Possible abuse attempt"
 *
 * The service worker already serialises its OWN refreshes, but that guard is a
 * promise inside one worker and cannot see a browser tab in another process.
 * The only reliable fix is for exactly one side to refresh, and it has to be the
 * extension, which is the one that must survive for days unattended.
 *
 * persistSession is the load-bearing flag, not autoRefreshToken. Turning off
 * auto-refresh only stops the background ticker; getSession() refreshes an
 * expired stored session regardless, and AuthProvider calls getSession() on
 * every page mount. With persistSession off, auth-js swaps in an in-memory
 * adapter, so a new page load finds no session and has nothing to refresh.
 *
 * The cost: the web app holds a session for one page load only. That is enough
 * for what it still does - sign in, verify, hand over - and a hard refresh
 * mid-flow means signing in again.
 *
 * Do NOT "fix" a stale web session here with signOut(). Every scope, including
 * "local", POSTs /logout and revokes this session server-side - which is the
 * extension's session too.
 */
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: false,
    autoRefreshToken: false,
  },
});
